/**
 * DepartmentDirectoryCard — Admin-only card mounted on ProfilePage showing
 * every NECS department + its PBX extension. Each row is a `tel:` link with
 * the RFC-3966 pause convention (`+18882413077,,105`) so mobile dialers
 * auto-key the extension after the main line picks up.
 *
 * Mirrors `NECS_DEPARTMENT_EXTENSIONS` in ProfilePage.js — keep them in
 * sync when a new department is added.
 */
import { Building2, Phone } from 'lucide-react';

const NECS_MAIN_LINE = '+18882413077';
const NECS_MAIN_LINE_DISPLAY = '+1 (888) 241-3077';

const DIRECTORY = [
  { code: 'EXC', name: 'Executive Office C-Suite', ext: '101' },
  { code: 'OPS', name: 'Operations', ext: '102' },
  { code: 'FIN', name: 'Finance & Accounting', ext: '103' },
  { code: 'HR',  name: 'Human Resources', ext: '104' },
  { code: 'IT',  name: 'Information Technology', ext: '105' },
  { code: 'LGL', name: 'Legal & Compliance', ext: '106' },
  { code: 'SLS', name: 'Sales & Business Development', ext: '107' },
  { code: 'MKT', name: 'Marketing & Communications', ext: '108' },
  { code: 'CS',  name: 'Customer Service', ext: '109' },
  { code: 'PRC', name: 'Procurement & Supply', ext: '110' },
  { code: 'SFY', name: 'Safety & Risk Management', ext: '111' },
  { code: 'TRN', name: 'Training & Development', ext: '112' },
];

export default function DepartmentDirectoryCard({ myDepartment }) {
  return (
    <div
      className="rounded-xl p-5 border"
      style={{
        backgroundColor: '#000000',
        borderColor: '#C9A962',
        boxShadow: '0 12px 28px rgba(0,0,0,0.35)',
      }}
      data-testid="department-directory-card"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Building2 className="h-5 w-5" style={{ color: '#C9A962' }} />
          <h3
            className="text-lg font-bold"
            style={{
              fontFamily: 'Barlow Condensed, sans-serif',
              color: '#C9A962',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
            }}
          >
            NECS Department Directory
          </h3>
        </div>
        <a
          href={`tel:${NECS_MAIN_LINE}`}
          className="text-xs font-mono"
          style={{ color: '#C9A962', textDecoration: 'none' }}
          data-testid="directory-main-line"
          title="Main line"
        >
          {NECS_MAIN_LINE_DISPLAY}
        </a>
      </div>

      <p className="text-[11px] text-zinc-400 mb-3">
        Tap any row on your phone to dial the department directly. Extensions
        auto-play after the main line answers.
      </p>

      <div
        className="grid grid-cols-1 sm:grid-cols-2 gap-2"
        data-testid="directory-grid"
      >
        {DIRECTORY.map((d) => {
          const isMine = myDepartment && d.name.toLowerCase() === myDepartment.toLowerCase();
          return (
            <a
              key={d.ext}
              href={`tel:${NECS_MAIN_LINE},,${d.ext}`}
              className="flex items-center justify-between gap-2 rounded-md px-3 py-2 transition"
              style={{
                backgroundColor: isMine ? '#C9A962' : 'rgba(255,255,255,0.05)',
                border: isMine ? '1px solid #C9A962' : '1px solid rgba(255,255,255,0.08)',
                color: isMine ? '#000000' : '#e4e4e7',
                textDecoration: 'none',
              }}
              data-testid={`directory-row-${d.code}`}
              title={`Call ${d.name} · ext ${d.ext}`}
            >
              <span className="flex items-center gap-2 min-w-0">
                <Phone
                  className="h-3.5 w-3.5 shrink-0"
                  style={{ color: isMine ? '#000' : '#C9A962' }}
                />
                <span className="truncate text-[13px] font-semibold">{d.name}</span>
              </span>
              <span
                className="font-mono text-xs shrink-0"
                style={{ color: isMine ? '#000' : '#C9A962' }}
              >
                ext. {d.ext}
              </span>
            </a>
          );
        })}
      </div>
    </div>
  );
}
